<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>
          Retrouvez le billet du voyageur par sa référence ou dans la liste des réservations, puis enregistrez ses
          bagages.
        </span>
      </div>
      <div class="ov-header__actions">
        <div class="st-tabs">
          <button
            type="button"
            class="st-tab"
            :class="{ 'st-tab--active': activeTab === 0 }"
            @click="activeTab = 0"
          >
            <v-icon small>mdi-magnify</v-icon>
            {{ $t('luggage.manualSearch') }}
          </button>
          <button
            type="button"
            class="st-tab"
            :class="{ 'st-tab--active': activeTab === 1 }"
            @click="activeTab = 1"
          >
            <v-icon small>mdi-format-list-bulleted</v-icon>
            {{ $t('luggage.reservations') }}
            <span class="ov-count">{{ ticketAvailableList.length }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Étapes du parcours -->
    <div class="st-steps">
      <div class="st-step st-step--active">
        <span class="st-step__num">1</span>
        <span class="st-step__label">{{ $t('luggage.searchTicket') }}</span>
      </div>
      <div class="st-step__line"></div>
      <div class="st-step">
        <span class="st-step__num">2</span>
        <span class="st-step__label">{{ $t('luggage.select') }}</span>
      </div>
      <div class="st-step__line"></div>
      <div class="st-step">
        <span class="st-step__num">3</span>
        <span class="st-step__label">{{ $t('luggage.registerBaggage') }}</span>
      </div>
    </div>

    <!-- TAB 0 : Recherche manuelle -->
    <div v-show="activeTab === 0" class="ov-grid">
      <div class="ov-card">
        <div class="ov-card__head">
          <div class="ov-card__title">
            <v-icon small color="#159e72">mdi-magnify</v-icon>
            {{ $t('luggage.searchByReference') }}
          </div>
        </div>
        <div class="ov-card__body">
          <div class="st-search">
            <div class="ov-field st-search__field">
              <label class="ov-label" for="st-keyword">{{ $t('luggage.ticketReference') }}</label>
              <div class="ov-search st-search__input">
                <v-icon small>mdi-ticket-confirmation-outline</v-icon>
                <input
                  id="st-keyword"
                  v-model.trim="keyword"
                  type="text"
                  class="ov-input"
                  :placeholder="$t('luggage.ticketPlaceholder')"
                  @keyup.enter="searchTicketByKeyword"
                  @focus="isInputFocused = true"
                  @blur="isInputFocused = false"
                />
              </div>
            </div>
            <button
              type="button"
              class="ov-btn ov-btn--primary"
              :disabled="!keyword || isSearching"
              @click="searchTicketByKeyword"
            >
              <v-progress-circular v-if="isSearching" indeterminate size="16" width="2"></v-progress-circular>
              <v-icon v-else small>mdi-magnify</v-icon>
              {{ $t('luggage.search') }}
            </button>
          </div>
        </div>
      </div>

      <aside class="ov-card ov-sticky">
        <div class="ov-card__head">
          <div class="ov-card__title">
            <v-icon small color="#159e72">mdi-lightbulb-on-outline</v-icon>
            {{ $t('luggage.tip') }}
          </div>
        </div>
        <div class="ov-card__body">
          <p class="st-tip">
            {{ $t('luggage.goToReservations') }}
            <strong>{{ $t('luggage.reservations') }}</strong>
            {{ $t('luggage.selectDirectly') }}
          </p>
          <ol class="st-tip-steps">
            <li>Cliquez sur <strong>Réservations</strong></li>
            <li>Trouvez le voyageur dans la liste</li>
            <li>Cliquez sur <strong>Sélectionner</strong></li>
          </ol>
        </div>
      </aside>
    </div>

    <!-- TAB 1 : Liste des réservations -->
    <div v-show="activeTab === 1" class="ov-card">
      <div class="ov-card__head">
        <div class="ov-card__title">
          {{ $t('luggage.reservations') }}
          <span class="ov-count">{{ ticketAvailableList.length }}</span>
        </div>
        <div class="ov-search">
          <v-icon small>mdi-magnify</v-icon>
          <input v-model="search" type="text" class="ov-input" :placeholder="$t('luggage.searchPlaceholder')" />
        </div>
      </div>
      <v-data-table
        :headers="headers"
        :items="ticketAvailableList"
        :loading="loading"
        loader-height="2"
        :search="search"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20],
          'items-per-page-text': $t('luggage.itemsPerPage') || 'Éléments par page:'
        }"
        loading-text="Chargement..."
        :no-data-text="$t('luggage.noReservations') || 'Aucune réservation'"
        class="ov-table"
      >
        <template v-slot:item="{ item }">
          <tr class="st-row" @click="chooseTicketFromList(item)">
            <td><span class="st-ref">{{ item.designation }}</span></td>
            <td>{{ item.clientDetails && item.clientDetails.nom }}</td>
            <td>{{ item.clientDetails && item.clientDetails.prenoms }}</td>
            <td>
              <button type="button" class="ov-btn ov-btn--primary ov-btn--sm" @click.stop="chooseTicketFromList(item)">
                <v-icon small>mdi-cursor-default-click-outline</v-icon>
                {{ $t('luggage.select') }}
              </button>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
  </div>
</template>

<script>
import {
  API_GET_RESERVATIONS_BY_ADMIN_TP,
  API_GET_RESERVATIONS_BY_SELLER,
  API_GET_TICKET_BY_REFERENCE,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import { showErrorMessage } from "@/modules/messages/messageProcess";
import axios from "axios";
export default {
  name: "SearchTicket",
  components: {},
  data() {
    return {
      activeTab: 0,
      isShowTicketList: false,
      isInputFocused: false,

      headers: [
        { text: this.$t('luggage.reference'), value: "designation" },
        { text: this.$t('luggage.lastName'), value: "clientDetails.nom" },
        { text: this.$t('luggage.firstName'), value: "clientDetails.prenoms" },
        { text: this.$t('luggage.actions'), value: "actions", sortable: false },
      ],

      search: "",

      loading: false,

      ticketFound:null,

      isSearching: false,
      keyword: null,
      errorMsg: null,
      successMsg: null,

      objectToSend: {
        data: {},
      },

      ticketSearching: {
        data: {
          designation: ""
        }
      },

      ticketAvailableList: [],
    };
  },

  methods: {
    // CHOOSE A TICKET FROM TICKET LIST
    chooseTicketFromList(item) {
      this.keyword = item.designation;
      this.$store.commit("STORE_TICKET", item);
      this.$router.push({ path: "/GestionBagage" });
    },
    //HIDE OR SHOW RESERVATIONS LIST TICKET
    showOrHideTicketList() {
      this.isShowTicketList = !this.isShowTicketList;
    },
    //RÉCUPÉRER LA LISTE DES BILLETS DISPONIBLES.
    async getTicketAvailables() {
      await axios
        .post(
          this.$store.state.userAuthentified.roleCode ==
            "RoleUtiGareCompagnieTransport" ||
            this.$store.state.userAuthentified.roleCode == "RoleUtiBagagiste"
            ? API_GET_RESERVATIONS_BY_SELLER
            : API_GET_RESERVATIONS_BY_ADMIN_TP,
          this.objectToSend,
          {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          }
        )
        .then((response) => {
          if (response.status == 200) {
            if (response.data.hasError == false) {
              this.ticketAvailableList = response.data.items;
            }
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async getTicketByReference() {
      this.ticketSearching.data.designation = this.keyword;
      this.isSearching = true;
      await axios
        .post(
          API_GET_TICKET_BY_REFERENCE,
          this.ticketSearching,
          {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          }
        )
        .then((response) => {
          const code = String(response?.data?.status?.code || "");
          const hasError = response?.data?.hasError;
          const message = response?.data?.status?.message;

          if (response.status === 200 && hasError === false && code === "800") {
            this.ticketFound = response.data.item;
            this.$store.commit("STORE_TICKET", this.ticketFound);
            this.$router.push({ path: "/GestionBagage" });
          } else {
            this.ticketFound = null;
            this.errorMsg =
              message ||
              this.$t('luggage.searchError');
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.ticketFound = null;
          this.errorMsg =
            e?.response?.data?.status?.message ||
            e?.response?.data?.message ||
            e?.message ||
            this.$t('luggage.networkError');
          showErrorMessage();
        })
        .finally(() => {
          this.isSearching = false;
        });
    },

    searchTicketByKeyword() {
      if (this.keyword == null || this.keyword == "") {
        this.errorMsg = this.$t('luggage.enterReference');
        showErrorMessage();
      }else{
        this.getTicketByReference()
      }
    },

    //RÉCUPÉRER LA LISTE DES BILLETS DISPONIBLES.
    searchTicketByReference() {
      this.isSearching = true;
      if (this.keyword == null) {
        this.errorMsg = this.$t('luggage.enterReference');
        showErrorMessage();
      } else if (this.keyword == "") {
        this.errorMsg = this.$t('luggage.enterReference');
        showErrorMessage();
      } else {
        const found = this.ticketAvailableList.find(
          (element) => element.designation == this.keyword
        );
        if (found) {
          this.$store.commit("STORE_TICKET", found);
          this.$router.push({ path: "/GestionBagage" });
        } else {
          this.errorMsg = this.$t('luggage.noMatch');
          showErrorMessage();
        }
      }
      this.isSearching = false;
    },
  },

  mounted() {
    this.getTicketAvailables();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/admin/ui";

/* Onglets recherche / réservations */
.st-tabs {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  background: #f1f5f9;
  border-radius: 10px;
}

.st-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: $ov-muted;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  .v-icon {
    color: inherit !important;
  }

  &--active {
    background: #ffffff;
    color: $ov-green;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
  }
}

/* Étapes */
.st-steps {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px 22px;
  background: #ffffff;
  border: 1px solid $ov-line;
  border-radius: 16px;
}

.st-step {
  display: flex;
  align-items: center;
  gap: 10px;
  color: $ov-subtle;

  &--active {
    color: $ov-navy;
  }
}

.st-step__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 800;
  color: $ov-muted;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.14);

  .st-step--active & {
    color: #ffffff;
    background: $ov-navy;
    border-color: $ov-navy;
    box-shadow: 0 0 0 4px rgba(12, 36, 97, 0.1);
  }
}

.st-step__label {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.st-step__line {
  flex: 1;
  min-width: 16px;
  height: 2px;
  border-radius: 2px;
  background: rgba(15, 23, 42, 0.1);
}

/* Recherche par référence */
.st-search {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.st-search__field {
  flex: 1;
}

.st-search__input .ov-input {
  height: 44px;
}

/* Astuce */
.st-tip {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.6;
  color: $ov-muted;

  strong {
    color: $ov-green;
  }
}

.st-tip-steps {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.9;
  color: $ov-ink;

  li::marker {
    font-weight: 800;
    color: $ov-green;
  }
}

/* Tableau */
.st-row {
  cursor: pointer;
}

.st-ref {
  font-weight: 700;
  color: $ov-navy;
}

@media (max-width: 760px) {
  .st-steps .st-step__label {
    display: none;
  }

  .st-search {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
